import React, { useContext, useState, useEffect } from 'react';
import ModalContext from '../../../contexts/ModalContext';
import {
  ModalContainer,
  Title,
  Subtitle,
  TableTitle,
  Table,
  Th,
  Td,
  CheckboxInput,
  QtdInput,
  ActionsContainer,
  CancelButton,
  ConfirmButton
} from './style';
import { useNavigate } from 'react-router-dom';
import ApiService from '../../../services/ApiService';
import { FeedbackContext } from '../../../contexts/FeedbackContext';

const GenerateOrderInvoiceModal: React.FC = () => {
  const { selectedSteelOrder, setModalActive } = useContext(ModalContext);
  const { setFeedback } = useContext(FeedbackContext);
  const navigate = useNavigate();

  const [items, setItems] = useState<any[]>([]);

  useEffect(() => {
    const fetchEligibleItems = async () => {
      if (selectedSteelOrder) {
        try {
          const { data } = await ApiService.fiscal.getEligibleNfeItems(selectedSteelOrder.id);
          if (data && Array.isArray(data)) {
            setItems(data.map((item: any) => ({ ...item, selected: false, qtdNF: '' })));
          }
        } catch (error) {
          console.error('Erro ao buscar itens elegíveis do fiscal:', error);
        }
      }
    };
    fetchEligibleItems();
  }, [selectedSteelOrder]);

  if (!selectedSteelOrder) return null;

  const handleToggleItem = (index: number) => {
    setItems(prev => {
      const next = [...prev];
      next[index] = { ...next[index], selected: !next[index].selected };
      return next;
    });
  };

  const handleQtdChange = (index: number, value: string) => {
    setItems(prev => {
      const next = [...prev];
      next[index] = { ...next[index], qtdNF: value };
      return next;
    });
  };

  const handleConfirm = async () => {
    const selectedItems = items.filter(item => item.selected && Number(item.qtdNF) > 0);

    if (selectedItems.length === 0) {
      setFeedback({ message: 'Selecione ao menos um item e informe uma quantidade válida.', isOpen: true, statusOk: false });
      return;
    }

    try {
      const payload = {
        orderId: selectedSteelOrder.id,
        items: selectedItems.map(item => {
          const quantidade = Number(item.qtdNF);
          const valorUnitario = Number(item.priceDetails?.unitPrice || item.valorUnitario || item.unitPrice || item.priceKg || 0);
          return {
            orderItemId: item.id,
            quantidade,
            valorUnitario,
            valorTotal: quantidade * valorUnitario,
          };
        }),
      };

      const { data: invoice } = await ApiService.fiscal.createNfe(payload);

      setFeedback({ message: 'Nota Fiscal gerada com sucesso!', isOpen: true, statusOk: true });
      setModalActive(false);
      navigate(`/panel/invoice/edit/${invoice.id}`);
    } catch (error) {
      console.error('❌ Erro ao gerar NF:', error);
      setFeedback({ message: 'Houve um erro ao tentar gerar a NF.', isOpen: true, statusOk: false });
    }
  };

  const companyName = typeof selectedSteelOrder.client === 'string'
    ? selectedSteelOrder.client
    : (selectedSteelOrder.client?.fantasyName || selectedSteelOrder.client?.companyName || 'Cliente Desconhecido');

  return (
    <ModalContainer>
      <Title>Pedido {selectedSteelOrder.id}</Title>
      <Subtitle>Cliente: {companyName}</Subtitle>

      <TableTitle>Itens do pedido</TableTitle>

      <Table>
        <thead>
          <tr>
            <Th>Adicionar</Th>
            <Th>Item</Th>
            <Th>NCM</Th>
            <Th>Qtd disponível</Th>
            <Th>Qtd para NF</Th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, index) => (
            <tr key={index}>
              <Td>
                <CheckboxInput
                  type="checkbox"
                  checked={item.selected}
                  onChange={() => handleToggleItem(index)}
                />
              </Td>
              <Td>{item.ploomesProductName || item.offeredProduct?.product || item.product?.name || item.name || `Produto ${item.id || index}`}</Td>
              <Td>{item.offeredProduct?.ncm || item.product?.ncm || item.ncm || '—'}</Td>
              <Td>{item.quantidadeDisponivel ?? item.offeredProduct?.quantity ?? item.quantity}</Td>
              <Td>
                <QtdInput
                  type="number"
                  value={item.selected ? item.qtdNF : ''}
                  onChange={(e) => handleQtdChange(index, e.target.value)}
                  disabled={!item.selected}
                  min="1"
                  max={item.offeredProduct?.quantity || item.quantity}
                />
              </Td>
            </tr>
          ))}
          {items.length === 0 && (
            <tr>
              <Td colSpan={5}>Nenhum item encontrado neste pedido.</Td>
            </tr>
          )}
        </tbody>
      </Table>

      <ActionsContainer>
        <CancelButton onClick={() => setModalActive(false)}>Cancelar</CancelButton>
        <ConfirmButton onClick={handleConfirm} disabled={items.filter(i => i.selected).length === 0}>Gerar NF</ConfirmButton>
      </ActionsContainer>
    </ModalContainer>
  );
};

export default GenerateOrderInvoiceModal;
